import { type FormEvent, useState } from "react";

import { PRODUCT_NAME } from "@src/shared/brand";
import {
  Wizard,
  WizardPanel,
  WizardProgress,
} from "@src/shared/components/v2/Wizard";

import type {
  SignUpAccountValues,
  SignUpProvisioningView,
} from "../../../../application";
import { type SignUpStepId } from "../../../../domain";
import { AccountProfileStep } from "../../../components/AccountProfileStep";
import { EmailVerificationStep } from "../../../components/EmailVerificationStep";
import { LoopPreviewStep } from "../../../components/LoopPreviewStep";
import { MicrophoneStep } from "../../../components/MicrophoneStep";
import { SignUpStepFooter } from "../../../components/SignUpStepFooter";
import { SignUpStepLayout } from "../../../components/SignUpStepLayout";
import { TrustStep } from "../../../components/TrustStep";
import { WaitingForAccountPanel } from "../../../components/WaitingForAccountPanel";

export type {
  SignUpAccountValues,
  SignUpProvisioningView,
} from "../../../../application";

export interface SignUpPageV2Props {
  activeStep: SignUpStepId;
  accountError?: string | null;
  emailVerificationError?: string | null;
  verificationEmail?: string;
  initialValues?: Partial<SignUpAccountValues>;
  isAccountSubmitting?: boolean;
  isEmailVerificationChecking?: boolean;
  isEmailVerificationPending?: boolean;
  isVerificationEmailResending?: boolean;
  provisioningView?: SignUpProvisioningView;
  onCreateAccount?: (
    values: SignUpAccountValues,
  ) => boolean | void | Promise<boolean | void>;
  onCheckEmailVerification?: () => void;
  onResendVerificationEmail?: () => void;
  onStepChange?: (step: SignUpStepId) => void;
  onComplete?: () => void;
  onSkip?: () => void;
}

const DEFAULT_VALUES: SignUpAccountValues = {
  email: "",
  password: "",
  firstName: "",
  lastName: "",
};

function getInitialValues(
  initialValues: Partial<SignUpAccountValues> | undefined,
): SignUpAccountValues {
  return {
    ...DEFAULT_VALUES,
    ...initialValues,
  };
}

function canCreateAccount(values: SignUpAccountValues) {
  return (
    values.email.trim().length > 0 &&
    values.password.length >= 10 &&
    values.firstName.trim().length > 0 &&
    values.lastName.trim().length > 0
  );
}

function getPreviousStep(step: SignUpStepId): SignUpStepId | null {
  switch (step) {
    case "verify-email":
      return "profile";
    case "microphone":
      return "verify-email";
    case "preview":
      return "microphone";
    case "trust":
      return "preview";
    case "profile":
      return null;
  }
}

export function SignUpPageV2({
  activeStep,
  accountError = null,
  emailVerificationError = null,
  verificationEmail,
  initialValues,
  isAccountSubmitting = false,
  isEmailVerificationChecking = false,
  isEmailVerificationPending = false,
  isVerificationEmailResending = false,
  provisioningView = { state: "idle" },
  onCreateAccount,
  onCheckEmailVerification,
  onResendVerificationEmail,
  onStepChange,
  onComplete,
  onSkip,
}: SignUpPageV2Props) {
  const [values, setValues] = useState(() => getInitialValues(initialValues));
  const [isMicrophoneReady, setIsMicrophoneReady] = useState(false);
  const canSubmitAccount = canCreateAccount(values);
  const isWaitingForProvisioning = provisioningView.state === "pending";
  const canEnterAssistant = provisioningView.state === "succeeded";
  const hasSubmittedAccount =
    isEmailVerificationPending || provisioningView.state !== "idle";
  const displayedVerificationEmail = verificationEmail || values.email;

  const goToStep = (step: SignUpStepId) => {
    onStepChange?.(step);
  };

  const goBack = () => {
    const previousStep = getPreviousStep(activeStep);
    if (previousStep) goToStep(previousStep);
  };

  const skip = () => {
    onSkip?.();
  };

  const handleCreateAccount = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmitAccount || isAccountSubmitting) return;

    if (hasSubmittedAccount) {
      goToStep(isEmailVerificationPending ? "verify-email" : "microphone");
      return;
    }

    await onCreateAccount?.({
      ...values,
      email: values.email.trim(),
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
    });
  };

  return (
    <main className="min-h-full bg-background text-foreground">
      <Wizard
        className="min-h-full max-w-[560px] justify-start px-6 pb-10 pt-[26px]"
        onValueChange={(value) => goToStep(value as SignUpStepId)}
        steps={[
          {
            value: "profile",
            title: "Tell us who you are",
            description:
              "We tailor the note, the codes, and how gaps surface to match your specialty.",
          },
          {
            value: "verify-email",
            title: "Verify your email",
            description:
              "Use the link we send you to confirm this account belongs to you.",
          },
          {
            value: "microphone",
            title: "Let's check your microphone",
            description:
              "The scribe listens during the visit. Set it up now so you're never interrupted mid-visit.",
          },
          {
            value: "preview",
            title: "See it run, start to finish",
            description: `Type, speak, or upload a gap list. ${PRODUCT_NAME.singular} builds the agenda, checks it off as you talk, and hands back the note and codes.`,
          },
          {
            value: "trust",
            title: "Your data, handled carefully",
            description: "Here's what happens to a visit, and what never does.",
          },
        ]}
        value={activeStep}
      >
        <WizardProgress
          className="mb-[66px]"
          complete={activeStep === "trust"}
        />

        <WizardPanel value="profile">
          {() => (
            <form
              aria-label={`Create ${PRODUCT_NAME.singular} account`}
              className="flex w-full flex-col items-center"
              onSubmit={handleCreateAccount}
            >
              <SignUpStepLayout
                description="We tailor the note, the codes, and how gaps surface to match your specialty."
                footer={
                  <SignUpStepFooter
                    ctaDisabled={!canSubmitAccount || isAccountSubmitting}
                    ctaLabel="Continue"
                    ctaLoading={isAccountSubmitting}
                    hideBack
                    hideSkip
                    onSkip={skip}
                    submit
                  />
                }
                stepNumber={1}
                title="Tell us who you are"
                totalSteps={5}
              >
                <AccountProfileStep
                  error={accountError}
                  isSubmitting={isAccountSubmitting}
                  onChange={setValues}
                  values={values}
                />
              </SignUpStepLayout>
            </form>
          )}
        </WizardPanel>

        <WizardPanel value="verify-email">
          {() => (
            <SignUpStepLayout
              description="Use the link we send you to confirm this account belongs to you."
              footer={
                <SignUpStepFooter
                  ctaLabel="I've verified my email"
                  ctaLoading={isEmailVerificationChecking}
                  hideSkip
                  onBack={goBack}
                  onContinue={onCheckEmailVerification}
                />
              }
              stepNumber={2}
              title="Verify your email"
              totalSteps={5}
            >
              <EmailVerificationStep
                email={displayedVerificationEmail}
                error={emailVerificationError}
                isChecking={isEmailVerificationChecking}
                isResending={isVerificationEmailResending}
                onResend={onResendVerificationEmail}
              />
            </SignUpStepLayout>
          )}
        </WizardPanel>

        <WizardPanel value="microphone">
          {() => (
            <SignUpStepLayout
              description="The scribe listens during the visit. Set it up now so you're never interrupted mid-visit."
              footer={
                <SignUpStepFooter
                  ctaDisabled={!isMicrophoneReady}
                  ctaLabel="Continue"
                  onBack={goBack}
                  onContinue={() => goToStep("preview")}
                  onSecondary={() => goToStep("preview")}
                  onSkip={skip}
                  secondaryLabel={
                    isMicrophoneReady ? undefined : "I'll set this up later"
                  }
                />
              }
              stepNumber={3}
              title="Let's check your microphone"
              totalSteps={5}
            >
              <MicrophoneStep onReadyChange={setIsMicrophoneReady} />
            </SignUpStepLayout>
          )}
        </WizardPanel>

        <WizardPanel value="preview">
          {() => (
            <SignUpStepLayout
              description={`Type, speak, or upload a gap list. ${PRODUCT_NAME.singular} builds the agenda, checks it off as you talk, and hands back the note and codes.`}
              footer={
                <SignUpStepFooter
                  ctaLabel="Continue"
                  onBack={goBack}
                  onContinue={() => goToStep("trust")}
                  onSkip={skip}
                />
              }
              stepNumber={4}
              title="See it run, start to finish"
              totalSteps={5}
            >
              <LoopPreviewStep />
            </SignUpStepLayout>
          )}
        </WizardPanel>

        <WizardPanel value="trust">
          {() => (
            <SignUpStepLayout
              description="Here's what happens to a visit, and what never does."
              footer={
                isWaitingForProvisioning ? null : (
                  <SignUpStepFooter
                    ctaDisabled={!canEnterAssistant}
                    ctaLabel={`Enter ${PRODUCT_NAME.singular}`}
                    onBack={goBack}
                    onContinue={onComplete}
                    onSkip={skip}
                  />
                )
              }
              stepNumber={5}
              title="Your data, handled carefully"
              totalSteps={5}
            >
              {isWaitingForProvisioning ? (
                <WaitingForAccountPanel message={provisioningView.message} />
              ) : (
                <TrustStep provisioningView={provisioningView} />
              )}
            </SignUpStepLayout>
          )}
        </WizardPanel>

        <div
          aria-atomic="true"
          aria-live="polite"
          className="sr-only"
          role="status"
        >
          {"message" in provisioningView
            ? (provisioningView.message ?? "")
            : ""}
        </div>
      </Wizard>
    </main>
  );
}
